import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { Dispatch } from '@reduxjs/toolkit';

import { appConfig } from '@/config/app.config';
import { sessionCleared, sessionEstablished } from '@/features/auth/authSlice';
import type { SessionData } from '@/api/generated/authApi';
import { ErrorCodes } from '@/types/api';
import type { ErrorData, NormalisedError } from '@/types/api';

export const CORRELATION_ID_HEADER = 'X-Correlation-Id';

/** Names set by Spring's CookieCsrfTokenRepository / CsrfCookieFilter (backend SecurityConfig). */
const CSRF_COOKIE = 'XSRF-TOKEN';
const CSRF_HEADER = 'X-XSRF-TOKEN';

/**
 * Serialises refresh attempts. Without it, five queries failing 401 at once fire five refresh
 * requests; with rotation and reuse detection on the backend (brief §9), four of those present
 * an already-rotated token and the whole session is revoked as a suspected replay.
 */
let refreshInFlight: Promise<boolean> | null = null;

const rawBaseQuery = fetchBaseQuery({
  // No version prefix: generated endpoint paths already include /api/v1.
  baseUrl: appConfig.api.baseUrl,
  // Send cookies. Authentication itself rides the Authorization header, but the CSRF-protected
  // mutations (logout) validate by double-submit: the XSRF-TOKEN cookie must travel WITH the
  // X-XSRF-TOKEN header for the server to compare them. fetchBaseQuery omits cookies by default,
  // so without this logout returned 403 and the refresh-token family was never revoked server
  // side - the session lived on. The refresh-token cookie is path-scoped to /api/v1/auth, so it
  // is only ever sent where it is needed.
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as { auth?: { accessToken?: string } }).auth?.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    // Echo the CSRF token on every request. It is not a credential (the double-submit check
    // only proves the caller can read a same-origin cookie), so sending it on safe methods is
    // harmless - the server enforces CSRF only on the protected mutations (logout). Sending it
    // unconditionally rather than gating on the request type removes any dependency on RTK
    // Query's `type` flag being what we expect, which is exactly what bit the logout call.
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) {
      headers.set(CSRF_HEADER, csrf);
    }
    return headers;
  },
});

/**
 * The base query every generated endpoint runs through.
 *
 * RTK Query already gives us the three things the brief asks for at once: endpoints living in
 * the Redux store, one service file per domain via `injectEndpoints`, and automatic
 * cancellation — every request carries an `AbortSignal` that fires on unmount, so unmounting a
 * screen mid-load cancels its requests without a line of component code.
 */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  // A 401 from an auth endpoint is a genuine failure (a wrong OTP, an expired refresh cookie),
  // not an expired access token, so refreshing and retrying would loop. Everything else that
  // 401s gets one silent refresh and one retry.
  if (result.error?.status === 401 && !isAuthEndpoint(args)) {
    const refreshed = await refreshSession(api.dispatch);
    if (refreshed) {
      result = await rawBaseQuery(args, api, extraOptions);
    }
  }

  return result;
};

function isAuthEndpoint(args: string | FetchArgs): boolean {
  const url = typeof args === 'string' ? args : args.url;
  return url.includes('/auth/');
}

/**
 * Rotates the refresh token and stores the new access token.
 *
 * <p>Done with a bare {@code fetch} rather than an RTK Query endpoint on purpose: it must set
 * the CSRF header by hand (refresh is CSRF-protected — it rides the httpOnly cookie), and
 * routing it through the cache would make a token rotation look like cacheable data. All
 * concurrent callers share one in-flight promise so rotation happens exactly once; a stampede
 * of parallel refreshes would present already-rotated tokens and trip reuse detection, revoking
 * the whole family.
 *
 * <p>Exported because app startup calls it too: the access token is memory-only, so a reload
 * has nothing until this re-establishes the session from the surviving cookie.
 */
export function refreshSession(dispatch: Dispatch): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      let response = await postRefresh();

      // A 403 is a CSRF rejection, which happens before the controller runs, so no refresh
      // token was consumed and a retry is safe. It occurs when the XSRF-TOKEN cookie the SPA
      // holds is out of sync with the server - a stale cookie left by an earlier deployment,
      // two cookies of the same name on different paths, or simply no token yet on a cold load.
      // The rejected response itself carries a fresh, correct cookie (CsrfCookieFilter renders
      // it on every response), so reading it back and retrying once resolves the desync.
      if (response.status === 403) {
        response = await postRefresh();
      }

      if (!response.ok) {
        dispatch(sessionCleared());
        return false;
      }

      const session = (await response.json()) as SessionData;
      if (!session?.accessToken) {
        dispatch(sessionCleared());
        return false;
      }

      dispatch(sessionEstablished(session));
      return true;
    } catch {
      // Network failure during refresh: treat as signed-out rather than wedging the app in a
      // half-authenticated state.
      dispatch(sessionCleared());
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * One refresh POST. The CSRF header is read fresh on each call so a retry picks up the cookie
 * the previous response just set.
 *
 * <p>A raw {@code fetch}, not RTK Query (which would cache a token rotation) and not axiosClient
 * (importing it here would close an import cycle: baseQuery -> axiosClient -> store -> baseApi
 * -> baseQuery). This one auth call is the deliberate exception to that rule.
 */
async function postRefresh(): Promise<Response> {
  const csrf = readCookie(CSRF_COOKIE);
  // eslint-disable-next-line no-restricted-globals
  return fetch(`${appConfig.api.baseUrl}/api/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: csrf ? { [CSRF_HEADER]: csrf } : {},
  });
}

/** Reads a non-httpOnly cookie by name. The CSRF cookie is readable by design; see backend. */
function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((entry) => entry.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

/**
 * Flattens any failure into one shape.
 *
 * Components should never branch on RTK Query's `FetchBaseQueryError` union directly — the
 * difference between a 409 `ErrorData`, a parsing failure and a dead network is three different
 * shapes, and handling them at every call site is how inconsistent error UX happens.
 */
export function normaliseError(error: unknown): NormalisedError {
  if (!error || typeof error !== 'object' || !('status' in error)) {
    return {
      code: ErrorCodes.UNEXPECTED,
      message: 'Something went wrong.',
    };
  }

  const fetchError = error as FetchBaseQueryError;

  // No HTTP response at all: offline, DNS failure, CORS rejection, or an aborted request.
  if (typeof fetchError.status === 'string') {
    return {
      code: 'NETWORK_ERROR',
      message:
        fetchError.status === 'TIMEOUT_ERROR'
          ? 'The server took too long to respond.'
          : 'Could not reach the server. Check your connection and try again.',
    };
  }

  // Anything else (an HTML error page from a proxy, say) gets the generic message.
  const body = isErrorData(fetchError.data) ? fetchError.data : undefined;

  return {
    code: body?.code ?? ErrorCodes.UNEXPECTED,
    message: body?.summary ?? `Request failed with status ${fetchError.status}.`,
    details: body?.details,
    correlationId: body?.traceId,
    status: fetchError.status,
  };
}

/** True for the backend's error body; every API error is one. */
export function isErrorData(value: unknown): value is ErrorData {
  return (
    typeof value === 'object' && value !== null && typeof (value as ErrorData).code === 'string'
  );
}
